import { Component } from "react";
import { connect } from "react-redux";
import { type InjectedFormProps, SubmissionError, reduxForm } from "redux-form";

import { type InputUserAccountInput, type License, type Role, type User } from "../../graphql.js";
import { loadRoles } from "../../redux/modules/roles-admin.js";
import { modifyUser } from "../../redux/modules/users-admin.js";
import { type IAppState } from "../../redux/reducer.js";
import { Dialog } from "../core/overlays/Dialogs.js";
import InputField, { InputReduxFormField } from "../core/redux-form/InputField.js";
import { AddNewUserDialogImpl } from "./AddNewUserDialog.js";
import { AddNewUserToDatabaseFormImpl } from "./AddNewUserToDatabaseForm.js";
import UserEditingFields from "./UserEditingFields.js";
import { SelForm } from "@/ui-modules/core/form/layout/Form.js";

interface IEditUserDialogOwnProps {
  user: User;
  onClose: () => void;
  refreshListOfUsers: () => void;
}

interface IEditUserDialogAppStateProps {
  roles: Role[];
  initialValues: Partial<any>;
  authenticationMode: string;
  license: License;
}

interface IEditUserDialogDispatchProps {
  loadRoles: () => Promise<Role>;
  modifyUser: (userInput: InputUserAccountInput) => Promise<boolean>;
}

export interface IEditUserDialogProps
  extends IEditUserDialogAppStateProps,
    IEditUserDialogDispatchProps,
    IEditUserDialogOwnProps,
    InjectedFormProps<any, any> {}

function mapStateToProps(state: IAppState, ownProps: IEditUserDialogOwnProps): IEditUserDialogAppStateProps {
  const user: User = ownProps.user;
  return {
    roles: state.rolesAdmin.roles,
    authenticationMode: state.config.configuration?.authenticationMode,
    license: state.config.configuration?.license,
    initialValues: {
      [AddNewUserToDatabaseFormImpl.ADD_USER_TO_DATABASE_FORM_FIELD_USERNAME]: user.username,
      [AddNewUserToDatabaseFormImpl.ADD_USER_TO_DATABASE_FORM_FIELD_FIRSTNAME]: user.firstName,
      [AddNewUserToDatabaseFormImpl.ADD_USER_TO_DATABASE_FORM_FIELD_LASTNAME]: user.lastName,
      [AddNewUserToDatabaseFormImpl.ADD_USER_TO_DATABASE_FORM_FIELD_EMAIL]: user.email,
      [AddNewUserToDatabaseFormImpl.ADD_USER_TO_DATABASE_FORM_FIELD_ROLES]: user.roles,
      [AddNewUserToDatabaseFormImpl.ADD_USER_TO_DATABASE_FORM_FIELD_STATUS]: user.isActive
        ? AddNewUserDialogImpl.statusOptions[0]
        : AddNewUserDialogImpl.statusOptions[1],
    },
  };
}

function mapDispatchToProps(dispatch): IEditUserDialogDispatchProps {
  return {
    loadRoles: () => dispatch(loadRoles()),
    modifyUser: (userInput: InputUserAccountInput) => dispatch(modifyUser(userInput)),
  };
}

class EditUserDialogImpl extends Component<IEditUserDialogProps> {
  public static EDIT_USER_FORM_NAME = "editUserForm";

  constructor(props: IEditUserDialogProps) {
    super(props);
  }

  componentDidMount() {
    this.props.loadRoles();
  }

  handleSubmitData = (values): Promise<boolean> => {
    const payload: InputUserAccountInput = {
      userId: this.props.user.id,
      username: this.props.user.username,
      firstName: values[AddNewUserToDatabaseFormImpl.ADD_USER_TO_DATABASE_FORM_FIELD_FIRSTNAME],
      lastName: values[AddNewUserToDatabaseFormImpl.ADD_USER_TO_DATABASE_FORM_FIELD_LASTNAME],
      email: values[AddNewUserToDatabaseFormImpl.ADD_USER_TO_DATABASE_FORM_FIELD_EMAIL],
      roles: values[AddNewUserToDatabaseFormImpl.ADD_USER_TO_DATABASE_FORM_FIELD_ROLES].map((role: Role) => role.id),
      isActive: values[AddNewUserToDatabaseFormImpl.ADD_USER_TO_DATABASE_FORM_FIELD_STATUS].value === 1,
      authenticationMode: this.props.authenticationMode,
    };

    return this.props.modifyUser(payload).then(
      (user: any) => {
        this.props.refreshListOfUsers();
        this.props.onClose();
        return user;
      },
      // the following will throw an error that redux-form understands as a submission error, with a general error that will be
      // available from this.props.error
      (rejected: Error) => {
        throw new SubmissionError({ _error: rejected.message });
      },
    );
  };

  render() {
    return (
      <Dialog
        mainActionButtonProps={{
          children: "Update",
          isLoading: this.props.submitting,
          isDisabled: this.props.submitting,
          onPress: this.props.handleSubmit(this.handleSubmitData),
        }}
        headerProps={{ title: "Account Details" }}
        closeFn={this.props.onClose}
        footerMessage={{ message: this.props.error }}
      >
        <SelForm>
          <InputReduxFormField
            componentClass="input"
            name={AddNewUserToDatabaseFormImpl.ADD_USER_TO_DATABASE_FORM_FIELD_USERNAME}
            label={"User ID"}
            disabled={true}
            placeholder={`Enter a unique ID for the account`}
            component={InputField}
          />

          <UserEditingFields
            user={this.props.user}
            roles={this.props.roles}
            authenticationMode={this.props.authenticationMode}
          />
        </SelForm>
      </Dialog>
    );
  }
}

const EditUserDialog = connect<IEditUserDialogAppStateProps, IEditUserDialogDispatchProps, IEditUserDialogOwnProps>(
  mapStateToProps,
  mapDispatchToProps,
)(
  reduxForm({
    form: EditUserDialogImpl.EDIT_USER_FORM_NAME,
  })(EditUserDialogImpl) as any,
);

export { EditUserDialogImpl };
export default EditUserDialog;
